/**
 * SanskritGame — वार्तालाप-पत्र लकार खेल
 * Option D: Card-based spoken Sanskrit practice via Hindi prompts.
 * 10 levels mapped to 10 लकार. Self-rated flashcard loop.
 */

import { useEffect, useState, useCallback } from 'react';
import { useGlossary } from '../contexts/GlossaryContext';
import '../styles/SanskritGame.css';

// ── Types ─────────────────────────────────────────────────────────────
interface GameCard {
  id: string;
  level: number;
  lakar: string;
  lakar_meaning: string;
  dhatu: string;
  dhatu_meaning: string;
  gana: string;
  purusha: string;
  vachan: string;
  pronoun: string;
  hindi_prompt: string;
  correct_form: string;
  shloka: string | null;
  shloka_hindi: string | null;
}

interface LevelInfo {
  level: number;
  lakar: string;
  lakar_meaning: string;
  card_count: number;
}

interface TokenState {
  gold: number;
  danda: number;
  total: number;
}

type GamePhase = 'home' | 'playing' | 'answer' | 'complete';

interface SessionState {
  level: number;
  lakar: string;
  lakar_meaning: string;
  cards: GameCard[];
  currentIndex: number;
  correct: number;
  partial: number;
  incorrect: number;
}

const CARDS_PER_SESSION = 20;
const STORAGE_KEY = 'sanskrit_game_tokens';

// ── Token helpers ─────────────────────────────────────────────────────
function loadTokens(): TokenState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { gold: 0, danda: 0, total: 0 };
}

function saveTokens(t: TokenState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
}

// ── Sub-components ────────────────────────────────────────────────────

function TokenBar({ tokens, t }: { tokens: TokenState; t: ReturnType<typeof useGlossary>['t'] }) {
  return (
    <div className="token-bar">
      <span className="token-bar-item">🏅 {t('sg.token.gold')}: <strong>{tokens.gold}</strong></span>
      <span className="token-bar-item">⚔️ {t('sg.token.danda')}: <strong>{tokens.danda}</strong></span>
    </div>
  );
}

function GameHome({
  levels,
  tokens,
  completedLevels,
  onSelectLevel,
  t,
}: {
  levels: LevelInfo[];
  tokens: TokenState;
  completedLevels: Set<number>;
  onSelectLevel: (level: LevelInfo) => void;
  t: ReturnType<typeof useGlossary>['t'];
}) {
  return (
    <div className="game-home">
      <h1>{t('sg.home.title')}</h1>
      <p className="game-subtitle">{t('sg.home.subtitle')}</p>
      <div className="token-summary">
        <div className="token-badge">
          <span className="token-icon">🏅</span>
          <span className="token-count">{tokens.gold}</span>
          <span className="token-label">{t('sg.token.gold')}</span>
        </div>
        <div className="token-badge">
          <span className="token-icon">⚔️</span>
          <span className="token-count">{tokens.danda}</span>
          <span className="token-label">{t('sg.token.danda')}</span>
        </div>
      </div>
      <div className="level-grid">
        {levels.map((lv) => (
          <div
            key={lv.level}
            className={`level-card${completedLevels.has(lv.level) ? ' completed' : ''}`}
            data-level={lv.level}
            onClick={() => onSelectLevel(lv)}
          >
            <div className="level-num">{t('sg.level.label', { level: lv.level })}</div>
            <div className="level-lakar">{lv.lakar}</div>
            <div className="level-meaning">{lv.lakar_meaning}</div>
            <div className="level-count">{t('sg.level.cards', { count: lv.card_count })}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CardView({
  session,
  onShowAnswer,
  onQuit,
  t,
}: {
  session: SessionState;
  onShowAnswer: () => void;
  onQuit: () => void;
  t: ReturnType<typeof useGlossary>['t'];
}) {
  const card = session.cards[session.currentIndex];
  const total = session.cards.length;
  const pct = Math.round((session.currentIndex / total) * 100);

  return (
    <div className="card-view">
      <TokenBar tokens={loadTokens()} t={t} />
      <div className="game-progress-bar">
        <div className="progress-info">
          <span>{session.lakar} — {t('sg.level.label', { level: session.level })}</span>
          <span>{session.currentIndex + 1} / {total}</span>
        </div>
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${pct}%` }} data-level={session.level} />
        </div>
      </div>

      <div className="flash-card" data-level={session.level}>
        <span className="card-lakar-badge">{session.lakar} · {session.lakar_meaning}</span>
        <div className="card-prompt">{card.hindi_prompt}</div>
        <div className="card-hint">
          गण: {card.gana} &nbsp;|&nbsp; {card.purusha} पुरुष · {card.vachan}
        </div>
      </div>

      <div className="card-actions">
          <button className="btn-primary" onClick={onShowAnswer}>
           {t('sg.button.showAnswer')} →
          </button>
          <button className="btn-secondary" onClick={onQuit}>
          ✕ {t('sg.button.quit')}
          </button>
      </div>
    </div>
  );
}

function AnswerView({
  session,
  onRate,
  t,
}: {
  session: SessionState;
  onRate: (rating: 'correct' | 'partial' | 'incorrect') => void;
  t: ReturnType<typeof useGlossary>['t'];
}) {
  const card = session.cards[session.currentIndex];

  return (
    <div className="answer-view">
      <div className="answer-card" data-level={session.level} style={{ borderTopColor: undefined }}>
        <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.5rem' }}>
          {session.lakar} · {card.purusha} · {card.vachan}
        </div>
        <div className="correct-form">{card.correct_form}</div>
        <div className="form-breakdown">
          {card.pronoun} + {card.dhatu} ({card.dhatu_meaning})
        </div>

        {card.shloka && (
          <div className="shloka-block">
            <div className="shloka-label">📜 {t('sg.answer.example')}</div>
            <div className="shloka-text">{card.shloka}</div>
            {card.shloka_hindi && (
              <div className="shloka-hindi">{t('sg.answer.meaning')}: {card.shloka_hindi}</div>
            )}
          </div>
        )}
      </div>

      <p className="self-rate-prompt">{t('sg.answer.prompt')}</p>
      <div className="rating-buttons">
        <button className="rating-btn correct" onClick={() => onRate('correct')}>
          <span className="rating-icon">🏅</span>
          <span className="rating-label">{t('sg.rating.correct')}</span>
        </button>
        <button className="rating-btn partial" onClick={() => onRate('partial')}>
          <span className="rating-icon">🤔</span>
          <span className="rating-label">{t('sg.rating.partial')}</span>
        </button>
        <button className="rating-btn incorrect" onClick={() => onRate('incorrect')}>
          <span className="rating-icon">⚔️</span>
          <span className="rating-label">{t('sg.rating.incorrect')}</span>
        </button>
      </div>
    </div>
  );
}

function LevelComplete({
  session,
  tokens,
  earnedGold,
  earnedDanda,
  onContinue,
  onHome,
  t,
}: {
  session: SessionState;
  tokens: TokenState;
  earnedGold: number;
  earnedDanda: number;
  onContinue: () => void;
  onHome: () => void;
  t: ReturnType<typeof useGlossary>['t'];
}) {
  const total = session.cards.length;
  const pct = Math.round((session.correct / total) * 100);
  const trophy = pct >= 80 ? '🏆' : pct >= 50 ? '🎖️' : '📜';

  return (
    <div className="level-complete">
      <div className="trophy">{trophy}</div>
      <h2>{t('sg.complete.title', { lakar: session.lakar })}</h2>
      <p style={{ color: '#555' }}>
        {pct >= 80 ? t('sg.complete.excellent') :
         pct >= 50 ? t('sg.complete.good') :
         t('sg.complete.retry')}
      </p>
      <div className="score-summary">
        <div className="score-item">
          <span className="score-num" style={{ color: '#16a34a' }}>{session.correct}</span>
          <span className="score-lbl">✅ {t('sg.score.correct')}</span>
        </div>
        <div className="score-item">
          <span className="score-num" style={{ color: '#d97706' }}>{session.partial}</span>
          <span className="score-lbl">🤔 {t('sg.score.partial')}</span>
        </div>
        <div className="score-item">
          <span className="score-num" style={{ color: '#dc2626' }}>{session.incorrect}</span>
          <span className="score-lbl">❌ {t('sg.score.incorrect')}</span>
        </div>
      </div>
      <div className="token-summary">
        <div className="token-badge">
          <span className="token-icon">🏅</span>
          <span className="token-count">+{earnedGold}</span>
          <span className="token-label">{t('sg.token.earnedGold')}</span>
        </div>
        {earnedDanda > 0 && (
          <div className="token-badge">
            <span className="token-icon">⚔️</span>
            <span className="token-count">+{earnedDanda}</span>
            <span className="token-label">{t('sg.token.danda')}</span>
          </div>
        )}
        <div className="token-badge">
          <span className="token-icon">🏅</span>
          <span className="token-count">{tokens.gold}</span>
          <span className="token-label">{t('sg.token.totalGold')}</span>
        </div>
      </div>
      <div className="card-actions" style={{ marginTop: '1.5rem' }}>
        <button className="btn-primary" onClick={onContinue}>
          {t('sg.button.next')} →
        </button>
        <button className="btn-secondary" onClick={onHome}>
          🏠 {t('sg.button.home')}
        </button>
      </div>
    </div>
  );
}

// ── Main Game Component ───────────────────────────────────────────────
export default function SanskritGame() {
  const { t } = useGlossary();
  const [phase, setPhase] = useState<GamePhase>('home');
  const [levels, setLevels] = useState<LevelInfo[]>([]);
  const [session, setSession] = useState<SessionState | null>(null);
  const [tokens, setTokens] = useState<TokenState>(loadTokens());
  const [loading, setLoading] = useState(true);          // true until levels are fetched
  const [loadingMsg, setLoadingMsg] = useState(t('sg.loading.levels'));
  const [error, setError] = useState<string | null>(null);
  const [earnedGold, setEarnedGold] = useState(0);
  const [earnedDanda, setEarnedDanda] = useState(0);
  const [completedLevels, setCompletedLevels] = useState<Set<number>>(new Set());

  // Load levels on mount
  useEffect(() => {
    fetch('/api/game/levels')
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        setLevels(data.levels);
        setLoading(false);
      })
      .catch((e) => {
        setError(t('sg.error.levels', { error: e.message }));
        setLoading(false);
      });
  }, []);

  const startLevel = useCallback(async (lv: LevelInfo) => {
    setLoadingMsg(t('sg.loading.cards'));
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/game/cards?level=${lv.level}&limit=${CARDS_PER_SESSION}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSession({
        level: lv.level,
        lakar: lv.lakar,
        lakar_meaning: lv.lakar_meaning,
        cards: data.cards,
        currentIndex: 0,
        correct: 0,
        partial: 0,
        incorrect: 0,
      });
      setPhase('playing');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'अज्ञात त्रुटि';
      setError(t('sg.error.cards', { error: msg }));
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRate = useCallback((rating: 'correct' | 'partial' | 'incorrect') => {
    if (!session) return;

    const goldDelta = rating === 'correct' ? 3 : rating === 'partial' ? 1 : 0;
    const dandaDelta = rating === 'incorrect' ? 1 : 0;

    const newTokens = {
      gold: tokens.gold + goldDelta,
      danda: tokens.danda + dandaDelta,
      total: tokens.total + goldDelta,
    };
    setTokens(newTokens);
    saveTokens(newTokens);

    const nextIndex = session.currentIndex + 1;
    const updatedSession = {
      ...session,
      currentIndex: nextIndex,
      correct: session.correct + (rating === 'correct' ? 1 : 0),
      partial: session.partial + (rating === 'partial' ? 1 : 0),
      incorrect: session.incorrect + (rating === 'incorrect' ? 1 : 0),
    };

    if (nextIndex >= session.cards.length) {
      setSession(updatedSession);
      setEarnedGold(updatedSession.correct * 3 + updatedSession.partial);
      setEarnedDanda(updatedSession.incorrect);
      setCompletedLevels((prev) => new Set([...prev, session.level]));
      setPhase('complete');
    } else {
      setSession(updatedSession);
      setPhase('playing');
    }
  }, [session, tokens]);

  const handleContinueToNext = useCallback(() => {
    if (!session) return;
    const nextLevel = levels.find((l) => l.level === session.level + 1);
    if (nextLevel) startLevel(nextLevel);
    else setPhase('home');
  }, [session, levels, startLevel]);

  if (error) {
    return (
      <div className="sanskrit-game">
        <div style={{ textAlign: 'center', padding: '3rem', color: '#dc2626' }}>
          <p>⚠️ {error}</p>
          <button className="btn-secondary" onClick={() => { setError(null); setPhase('home'); }}>
            {t('sg.button.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="sanskrit-game">
        <div className="sg-loading">
          <div className="sg-spinner" />
          <p>{loadingMsg}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sanskrit-game">
      {/* Persistent controls: home + reset — shown during active gameplay */}
      {phase !== 'home' && (
        <div className="sg-game-controls">
          <button
            className="sgc-btn"
            onClick={() => setPhase('home')}
            title={t('sg.control.homeTitle')}
          >
            🏠 <span className="sgc-label">{t('sg.button.home')}</span>
          </button>
          <span className="sgc-title">{t('sg.home.title')}</span>
          <button
            className="sgc-btn sgc-reset"
            title={t('sg.control.resetTitle')}
            onClick={() => {
              const fresh = { gold: 0, danda: 0, total: 0 };
              saveTokens(fresh);
              setTokens(fresh);
              setCompletedLevels(new Set());
              setSession(null);
              setPhase('home');
            }}
          >
            🔄 <span className="sgc-label">{t('sg.button.reset')}</span>
          </button>
        </div>
      )}

      {phase === 'home' && (
          <GameHome
            levels={levels}
            tokens={tokens}
            completedLevels={completedLevels}
            onSelectLevel={startLevel}
            t={t}
          />
      )}

      {phase === 'playing' && session && (
        <CardView
          session={session}
          onShowAnswer={() => setPhase('answer')}
          onQuit={() => setPhase('home')}
          t={t}
        />
      )}

      {phase === 'answer' && session && (
        <AnswerView session={session} onRate={handleRate} t={t} />
      )}

      {phase === 'complete' && session && (
        <LevelComplete
          session={session}
          tokens={tokens}
          earnedGold={earnedGold}
          earnedDanda={earnedDanda}
          onContinue={handleContinueToNext}
          onHome={() => setPhase('home')}
          t={t}
        />
      )}
    </div>
  );
}
